import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { rupee, imageUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { ProductCard } from "../components/ProductCard";
import { VegBadge } from "../components/VegBadge";
import { ArrowRight, Star, Crown, Truck, Award, Clock, Quote } from "lucide-react";

function Section({ id, overline, title, subtitle, link, children }) {
  return (
    <section id={id} className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid={`section-${id}`}>
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          {overline && <div className="overline text-gold mb-2">{overline}</div>}
          <h2 className="font-serif text-3xl sm:text-4xl text-navy">{title}</h2>
          {subtitle && <p className="text-muted-foreground mt-2 max-w-xl text-sm">{subtitle}</p>}
        </div>
        {link && <Link to={link} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold transition-colors shrink-0" data-testid={`viewall-${id}`}>View all <ArrowRight size={16} /></Link>}
      </div>
      {children}
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState({});
  const [reviews, setReviews] = useState([]);
  const [cats, setCats] = useState([]);
  const { content } = useContent();
  const home = content?.home || {};
  const hero = home.hero || {};
  const combos = home.combos_banner || {};
  const party = home.party_banner || {};
  const aboutC = home.about || {};

  useEffect(() => {
    const q = (params) => api.get("/products", { params }).then((r) => r.data);
    Promise.all([
      q({ best_seller: true }), q({ category: "biryani" }), q({ category: "combos", featured: true }),
      q({ category: "tandoori" }), q({ category: "rolls" }), q({ category: "gravies" }),
      q({ category: "platters" }), q({ category: "desserts" }), q({ category: "beverages" }),
    ]).then(([best, biryani, combos, tandoori, rolls, gravies, platters, desserts, beverages]) =>
      setData({ best, biryani, combos, tandoori, rolls, gravies, platters, desserts, beverages }));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const grid = (list, n = 4) => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
      {(list || []).slice(0, n).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
    </div>
  );

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy overflow-hidden hero-motif" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8 items-center py-14 lg:py-20">
          <div className="fade-up">
            <div className="overline text-gold mb-4 flex items-center gap-2"><span className="gold-rule w-8" /> {hero.overline || "Royal Indian Kitchen"}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">{hero.title_line1 || "Royal Flavours."}<br /><span className="text-gold">{hero.title_line2 || "Timeless Tradition."}</span></h1>
            <p className="text-cream/75 mt-5 max-w-lg text-base leading-relaxed">{hero.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={hero.primary_link || "/menu"} data-testid="hero-order-now" className="btn-shine bg-gold text-navy font-semibold px-7 py-3.5 rounded-full hover:bg-gold-light transition-colors">{hero.primary_label || "Order Now"}</Link>
              <Link to={hero.secondary_link || "/menu"} data-testid="hero-explore-menu" className="border border-cream/30 text-cream font-semibold px-7 py-3.5 rounded-full hover:border-gold hover:text-gold transition-colors">{hero.secondary_label || "Explore Menu"}</Link>
            </div>
            <div className="flex gap-6 mt-10 text-cream/70">
              {[[Award, "Heritage Recipes"], [Truck, "Fast Delivery"], [Star, "4.8 Rated"]].map(([Icon, t], i) => (
                <div key={i} className="flex items-center gap-2 text-xs"><Icon size={16} className="text-gold" /> {t}</div>
              ))}
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="absolute -inset-3 border border-gold/30 rounded-2xl" />
            <img src={imageUrl(hero.image)} alt="Royal biryani" className="relative rounded-2xl w-full aspect-[4/3] object-cover shadow-2xl" />
            <div className="absolute -bottom-5 -left-5 bg-cream rounded-xl px-5 py-3 shadow-xl hidden sm:block">
              <div className="font-serif text-2xl text-navy">{hero.badge_number || "88+"}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{hero.badge_label || "Royal Dishes"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category strip */}
      <div className="bg-beige border-y border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-3 overflow-x-auto scrollbar-hide">
          {cats.map((c) => (
            <Link key={c.key} to={`/menu?category=${c.key}`} data-testid={`homecat-${c.key}`}
              className="shrink-0 flex items-center gap-2.5 bg-white rounded-full pl-2 pr-5 py-2 border border-black/5 card-hover">
              <img src={imageUrl(c.image)} alt={c.name} className="w-9 h-9 rounded-full object-cover" />
              <span className="text-sm font-medium text-navy whitespace-nowrap">{c.name}</span>
            </Link>
          ))}
        </div>
      </div>

      <Section id="best-sellers" overline="Loved by all" title="Best Sellers" subtitle="Our most-ordered royal dishes, decided by you." link="/menu?filter=best">{grid(data.best)}</Section>
      <Section id="signature-biryanis" overline="The crown jewel" title="Signature Biryanis" subtitle="Slow-cooked on dum with saffron and heritage spices." link="/menu?category=biryani">{grid(data.biryani)}</Section>

      {/* Combos banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="section-royal-combos">
        <div className="rounded-2xl bg-navy overflow-hidden grid md:grid-cols-2">
          <div className="p-10 lg:p-12 flex flex-col justify-center">
            <div className="overline text-gold mb-3">{combos.overline || "Complete meals"}</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-cream">{combos.title || "Royal Combos"}</h2>
            <p className="text-cream/70 mt-3 text-sm">{combos.text}</p>
            <Link to={combos.button_link || "/menu?category=combos"} className="mt-6 self-start btn-shine bg-gold text-navy font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors" data-testid="combos-cta">{combos.button_label || "Browse Combos"}</Link>
          </div>
          <div className="grid grid-cols-2 gap-3 p-6 bg-navy-soft">
            {(data.combos || []).slice(0, 4).map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="bg-white rounded-xl overflow-hidden card-hover">
                <img src={imageUrl(p.image)} alt={p.name} className="w-full aspect-square object-cover" />
                <div className="p-2.5"><div className="text-xs font-medium text-navy line-clamp-1">{p.name}</div><div className="text-xs text-gold font-semibold mt-0.5">{rupee(p.base_price)}</div></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Section id="from-the-tandoor" overline="From the clay oven" title="From the Tandoor" subtitle="Char-grilled kebabs & tikkas, smoky and succulent." link="/menu?category=tandoori">{grid(data.tandoori)}</Section>
      <Section id="rolls" overline="Rolled fresh" title="Signature Rolls" subtitle="Street-style rolls wrapped in flaky parathas." link="/menu?category=rolls">{grid(data.rolls)}</Section>
      <Section id="gravies" overline="Rich & royal" title="Signature Gravies" subtitle="Slow-cooked curries perfect with our breads." link="/menu?category=gravies">{grid(data.gravies)}</Section>

      {/* Party CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="section-platters">
        <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-beige to-cream p-10 lg:p-12 text-center">
          <Crown className="mx-auto text-gold mb-3" size={30} />
          <div className="overline text-gold">{party.overline || "Plan a party?"}</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-navy mt-2">{party.title || "Platters & Party Boxes"}</h2>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-sm">{party.text}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Link to={party.primary_link || "/menu?category=platters"} className="btn-shine bg-navy text-cream font-semibold px-6 py-3 rounded-full hover:bg-navy-soft transition-colors" data-testid="party-platters-cta">{party.primary_label || "Order Party Platters"}</Link>
            <Link to={party.secondary_link || "/party"} className="border border-navy/30 text-navy font-semibold px-6 py-3 rounded-full hover:border-gold transition-colors">{party.secondary_label || "Plan Your Party"}</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-9">{(data.platters || []).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </div>
      </section>

      <Section id="desserts" overline="Sweet endings" title="Desserts & Sides" link="/menu?category=desserts">{grid(data.desserts)}</Section>
      <Section id="beverages" overline="Coolers & classics" title="Beverages" link="/menu?category=beverages">{grid(data.beverages)}</Section>

      {/* Reviews */}
      <section className="bg-navy text-cream py-16" data-testid="section-reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="overline text-gold mb-2">Kind words</div>
            <h2 className="font-serif text-3xl sm:text-4xl">What Our Guests Say</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.map((r, i) => (
              <div key={i} className="bg-navy-soft rounded-xl p-6 border border-white/5">
                <Quote className="text-gold mb-3" size={22} />
                <div className="flex gap-0.5 mb-3">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={13} className="fill-gold text-gold" />)}</div>
                <p className="text-sm text-cream/80 leading-relaxed">"{r.text}"</p>
                <div className="mt-4 text-sm font-semibold text-gold">{r.name}</div>
                <div className="text-xs text-cream/50">{r.location}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center" data-testid="section-about">
        <div className="relative">
          <div className="absolute -inset-3 border border-gold/30 rounded-2xl" />
          <img src={imageUrl(aboutC.image)} alt="Royal spread" className="relative rounded-2xl w-full aspect-[4/3] object-cover" />
        </div>
        <div>
          <div className="overline text-gold mb-3">{aboutC.overline || "Our story"}</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-navy">{aboutC.title || "About Begum's Biryani Secrets"}</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed text-sm">{aboutC.text}</p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {(aboutC.stats || []).map((st, i) => (
              <div key={i} className="border-l-2 border-gold pl-3"><div className="font-serif text-2xl text-navy">{st.number}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">{st.label}</div></div>
            ))}
          </div>
          <Link to={aboutC.cta_link || "/catering"} className="inline-flex items-center gap-1.5 mt-8 text-sm font-semibold text-navy hover:text-gold transition-colors">{aboutC.cta_label || "Catering & Bulk Orders"} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
